import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type BeraMsg = { role: "user" | "assistant"; content: string; ts: number };

const SESSION_KEY = "bera_ai_session_msgs";

export function useBeraAI() {
  const [messages, setMessages] = useState<BeraMsg[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const userIdRef = useRef<string | null>(null);

  // Carrega sessão (visitante) — guarda em sessionStorage
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      userIdRef.current = data?.user?.id ?? null;
      if (!userIdRef.current) {
        try {
          const raw = sessionStorage.getItem(SESSION_KEY);
          if (raw) setMessages(JSON.parse(raw));
        } catch {}
      }
    })();
  }, []);

  useEffect(() => {
    if (!userIdRef.current) {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages));
      } catch {}
    }
  }, [messages]);

  const send = useCallback(async (text: string) => {
    const clean = text.trim();
    if (!clean || isStreaming) return;

    setError(null);
    const userMsg: BeraMsg = { role: "user", content: clean, ts: Date.now() };
    const assistantMsg: BeraMsg = { role: "assistant", content: "", ts: Date.now() };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bera-ai-chat`;

      const history = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const resp = await fetch(url, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          messages: history,
          conversation_id: conversationIdRef.current,
          // Honeypot: bots costumam preencher campos "hidden" — utilizadores
          // reais deixam sempre vazio. O servidor rejeita se vier preenchido.
          hp: "",
          client: "web",
        }),
      });

      if (!resp.ok || !resp.body) {
        if (resp.status === 429) throw new Error("Demasiados pedidos. Tenta daqui a uns minutos.");
        if (resp.status === 402) throw new Error("Serviço temporariamente indisponível.");
        if (resp.status === 403) throw new Error("Pedido bloqueado por segurança.");
        throw new Error("Não consegui responder agora. Tenta novamente.");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, idx);
          buf = buf.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data:")) continue;
          const json = line.slice(5).trim();
          if (json === "[DONE]" || !json) continue;
          try {
            const parsed = JSON.parse(json);
            if (parsed.meta?.conversation_id) {
              conversationIdRef.current = parsed.meta.conversation_id;
              continue;
            }
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              acc += delta;
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.role === "assistant") next[next.length - 1] = { ...last, content: acc };
                return next;
              });
            }
          } catch {
            buf = line + "\n" + buf;
            break;
          }
        }
      }
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      const msg = e?.message || "Erro inesperado";
      setError(msg);
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && !last.content) {
          next[next.length - 1] = { ...last, content: `⚠️ ${msg}` };
        }
        return next;
      });
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [messages, isStreaming]);

  const clear = useCallback(() => {
    setMessages([]);
    conversationIdRef.current = null;
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {}
  }, []);

  const escalate = useCallback(async (category: string, summary: string) => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token;
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bera-ai-escalate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          category,
          summary,
          conversation_id: conversationIdRef.current,
        }),
      });
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content:
            "Registei a tua situação para análise interna da equipa BeraStays. Vou continuar a apoiar-te aqui e serás atualizado dentro da plataforma assim que houver novidades.",
          ts: Date.now(),
        },
      ]);
    } catch (e) {
      setError("Não consegui encaminhar agora. Tenta novamente.");
    }
  }, []);

  return { messages, isStreaming, error, send, clear, escalate };
}
